import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faEdit,
    faTrash,
    faTimes
} from "@fortawesome/free-solid-svg-icons";

import Breadcrumb from "../../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../../components/Ui_Element/DotLoader";
import { post, getlist, deleteitem } from "../../../contexts/apicontexts";
import config from "../../../config";

const PAGE_SIZE = 10;

export default function Department() {
    const [departments, setDepartments] = useState([]);
    const [page, setPage] = useState(1);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoder, setIsLoder] = useState(false);
    const [errors, setErrors] = useState({});
    const [formState, setFormState] = useState({
        id: 0,
        name: "",
        code: "",
        description: "",
        sortOrder: 0
    });

    const validateForm = () => {
        const newErrors = {};

        if (!formState.name.trim()) {
            newErrors.name = "Department name is required";
        }

        if (!formState.code.trim()) {
            newErrors.code = "Code is required";
        }

        if (formState.sortOrder === "" || formState.sortOrder === null) {
            newErrors.sortOrder = "Sort order is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        setIsLoder(true);
        try {
            const response = await getlist(`${config.baseurl}Departments/getlist`);
            if (response?.status === 200) {
                setDepartments(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching departments:", error);
        } finally {
            setIsLoder(false);
        }
    };

    const openAddModal = () => {
        setFormState({
            id: 0,
            name: "",
            code: "",
            description: "",
            sortOrder: 0
        });
        setErrors({});
        setIsOpen(true);
    };

    const openEditModal = (dept) => {
        setFormState(dept);
        setErrors({});
        setIsOpen(true);
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsLoder(true);

        const payload = {
            id: formState.id || 0,
            name: formState.name.trim(),
            code: formState.code.trim(),
            description: formState.description || "",
            sortOrder: Number(formState.sortOrder) || 0
        };

        try {
            const url = payload.id > 0
                ? `${config.baseurl}Departments/update`
                : `${config.baseurl}Departments/add`;

            const response = await post(url, payload);

            if (response?.status === 200) {
                setIsOpen(false);
                setErrors({});
                fetchDepartments();
            } else {
                console.error("Save failed:", response);
            }
        } catch (error) {
            console.error("API Error:", error);
        } finally {
            setIsLoder(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this department?")) return;
        try {
            await deleteitem(`${config.baseurl}Departments/delete`, id);
            fetchDepartments();
        } catch (error) {
            console.error("Delete failed:", error);
        }
    };

    const paginatedData = departments.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-[#0F111A] min-h-screen transition-colors duration-300">
            <Breadcrumb pageName="Departments" currentPage="Departments" />

            <div className="bg-white dark:bg-[#161B22] rounded-xl shadow border dark:border-gray-800 mt-4 overflow-hidden">
                {/* HEADER */}
                <div className="card-header flex justify-between items-center px-4 py-3 sm:px-5 border-b transition-colors duration-300
                    bg-gradient-to-r from-[#C04C2A] to-[#4A1B13]
                    dark:from-[#2D120D] dark:to-[#1A1A1A] dark:border-gray-800">

                    <div className="flex items-center gap-3">
                        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                            Department List
                        </h2>
                        {isLoder && <DotLoader color="#ffffff" size="w-1.5 h-1.5" />}
                    </div>

                    <button
                        onClick={openAddModal}
                        className="flex items-center justify-center w-10 h-10 rounded-full transition-all
                        transform hover:scale-110 active:scale-95 shadow-lg
                        bg-white dark:bg-gray-200 text-[#C04C2A] dark:text-[#4A1B13]"
                    >
                        <FontAwesomeIcon icon={faPlus} className="text-lg" />
                    </button>
                </div>

                {/* TABLE */}
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px] sm:text-sm min-w-[600px] sm:min-w-full">
                        <thead className="bg-gray-100 dark:bg-gray-800/50 uppercase text-[10px] tracking-wider text-gray-500 dark:text-gray-400">
                            <tr>
                                <th className="p-3 text-left">Department Name</th>
                                <th className="p-3 text-left">Code</th>
                                <th className="p-3 text-left">Description</th>
                                <th className="p-3 text-center">Order</th>
                                <th className="p-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map(dept => (
                                    <tr key={dept.id} className="border-t dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-[#161B22]">
                                        <td className="p-3 font-medium text-gray-800 dark:text-gray-200">{dept.name}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400">{dept.code}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400">{dept.description}</td>
                                        <td className="p-3 text-center text-gray-600 dark:text-gray-400">{dept.sortOrder}</td>
                                        <td className="p-3 text-right space-x-3">
                                            <button
                                                onClick={() => openEditModal(dept)}
                                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                                                title="Edit"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(dept.id)}
                                                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                                                title="Delete"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-500 dark:text-gray-400 dark:bg-[#161B22]">
                                        No departments found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION INFO (Optional) */}
                {departments.length > PAGE_SIZE && (
                    <div className="p-4 border-t dark:border-gray-800 flex justify-end bg-gray-50/50 dark:bg-gray-800/20">
                        {/* Simple Pagination Controls could go here if needed */}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            Showing {paginatedData.length} of {departments.length}
                        </span>
                    </div>
                )}
            </div>

            {/* MODAL */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        onClick={() => setIsOpen(false)}
                    ></div>

                    {/* Modal Content */}
                    <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg p-5 sm:p-8 relative shadow-2xl
                     transform transition-all animate-in fade-in zoom-in duration-200">

                        {/* Close Button */}
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                        >
                            <FontAwesomeIcon icon={faTimes} size="lg" />
                        </button>

                        {/* Title */}
                        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white">
                            {formState.id ? "Update Department" : "Create Department"}
                        </h2>

                        {/* Form */}
                        <div className="space-y-5">
                            {/* Department Name */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Department Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                                            outline-none focus:ring-2
                                            ${errors.name
                                            ? "border-red-500 ring-red-100"
                                            : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                        }`}
                                    value={formState.name}
                                    placeholder="e.g. Human Resources"
                                    onChange={e => {
                                        setFormState({ ...formState, name: e.target.value });
                                        setErrors({ ...errors, name: "" });
                                    }}
                                />
                                {errors.name && (
                                    <p className="mt-1 text-xs text-red-500 font-medium">{errors.name}</p>
                                )}
                            </div>

                            {/* Code & Sort Order */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                                                outline-none focus:ring-2
                                                ${errors.code
                                                ? "border-red-500 ring-red-100"
                                                : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                            }`}
                                        value={formState.code}
                                        placeholder="e.g. HR"
                                        onChange={e => {
                                            setFormState({ ...formState, code: e.target.value });
                                            setErrors({ ...errors, code: "" });
                                        }}
                                    />
                                    {errors.code && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{errors.code}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Sort Order <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                                                outline-none focus:ring-2
                                                ${errors.sortOrder
                                                ? "border-red-500 ring-red-100"
                                                : "border-gray-400 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"
                                            }`}
                                        value={formState.sortOrder}
                                        onChange={e => {
                                            setFormState({ ...formState, sortOrder: e.target.value });
                                            setErrors({ ...errors, sortOrder: "" });
                                        }}
                                    />
                                    {errors.sortOrder && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{errors.sortOrder}</p>
                                    )}
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    className="w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white
                                        outline-none focus:ring-2 border-gray-400 dark:border-gray-700
                                        focus:ring-orange-100 focus:border-[#C04C2A] h-24 resize-none"
                                    value={formState.description}
                                    placeholder="Optional description..."
                                    onChange={e => setFormState({ ...formState, description: e.target.value })}
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="flex-1 px-4 py-3 border border-gray-200 dark:border-gray-700
                                        text-gray-600 dark:text-gray-300 font-bold rounded-xl
                                        hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={isLoder}
                                    className="flex-1 px-4 py-3 text-white font-bold rounded-xl
                                        shadow-lg transform transition-all active:scale-95 disabled:opacity-70"
                                    style={{
                                        background: "linear-gradient(to right, #C04C2A, #4A1B13)"
                                    }}
                                >
                                    {isLoder ? (
                                        <DotLoader color="#fff" size="w-1.5 h-1.5" />
                                    ) : (
                                        formState.id ? "Update" : "Save"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
